import React, { useState, useCallback } from 'react';
import { BaseAutocomplete } from '@askporter/component-library/BaseAutocomplete';
import { BaseAutocompleteValue } from '@askporter/component-library/RevampedAutocomplete/types';
import { SxProps, Theme } from '@mui/material/styles';
import { useInfiniteQuery } from '@tanstack/react-query';
import { SearchUserFilter, UserSearchResultExternal } from '@askporter/client-grieg-lyric';
import { queryKeys } from '@askporter/query-keys';
import { useDebounce, fullName, isAnonymousUser } from '@askporter/utils';
import { RevampedSingleUserAutocomplete } from '../RevampedAutocomplete';
import doUserSearch from './utils/doUserSearch';
type MultipleUserPropUnion =
  | {
      /**
       * The selected user for the autocomplete component
       */
      value: UserSearchResultExternal;
      /**
       * Whether the autocomplete allows multiple users to be selected
       */
      isMultiple?: false;
      /**
       * The function to call when the selected value changes (e.g. A user option is selected)
       */
      onChange: (user: UserSearchResultExternal) => void;
    }
  | {
      /**
       * The selected users for the autocomplete component
       */
      value: UserSearchResultExternal[];
      /**
       * Whether the autocomplete allows multiple users to be selected
       */
      isMultiple: true;
      /**
       * The function to call when the selected value changes(e.g. A user option is selected)
       */
      onChange: (user: UserSearchResultExternal[]) => void;
    };
export type UserAutocompleteProps = {
  /**
   * The translation function
   */
  t: (key: string, options?: Record<string, string | number>) => string;
  /**
   * The prefix for the id for the autocomplete component
   */
  idPrefix: string;
  /**
   * The label for the autocomplete component
   */
  label: string;
  /**
   * The placeholder for the autocomplete component
   */
  placeholder?: string;
  /**
   * Whether the autocomplete component is disabled
   */
  disabled?: boolean;
  /**
   * The function to determine whether to show the user in the list of users returned in the search request
   */
  showUser?: (user: UserSearchResultExternal) => boolean;
  /**
   * The filter to apply to the search request
   */
  filter?: SearchUserFilter;
  /**
   * Whether there is an error which should be displayed
   */
  error?: boolean;
  /**
   * The helper text to display
   */
  helperText?: string;
  /**
   * The adornment to display at the top of the list (e.g. create an unregistered user button)
   */
  topOfListAdornment?: React.ReactNode;
  /**
   * Optional function to call when the input value changes (e.g. when the user types in the input field)
   */
  onInputChange?: (newInputValue: string) => void;
  /**
   * The function to close the autocomplete programmatically
   */
  closeComponent?: {
    shouldClose: boolean;
    reset: () => void;
  };
  /**
   * Whether the autocomplete component is read only
   */
  readOnly?: boolean;
  /**
   * The text to display when the autocomplete component is read only
   */
  readOnlyText?: string;
  /**
   * Optional to set the input to use full width
   */
  fullWidth?: boolean;
  /**
   * The page size to use for the search request
   */
  pageSize?: number;
  /**
   * on blur event handler
   */
  onBlur?: React.FocusEventHandler<HTMLDivElement>;
  showAvatar?: boolean;
  /**
   * Optional sx for the autocomplete component
   */
  sx?: SxProps<Theme>;
} & MultipleUserPropUnion;
/**
 * An autocomplete component for selecting a user for a list of options with infinite scroll,
 * it hits `users/search` endpoint to get the list of users
 */
export const UserAutocomplete = ({
  idPrefix,
  label,
  placeholder = '',
  disabled,
  filter = {},
  error = false,
  helperText,
  showUser = () => true,
  topOfListAdornment,
  onInputChange,
  closeComponent,
  readOnly = false,
  readOnlyText = undefined,
  t,
  fullWidth = false,
  pageSize,
  isMultiple,
  value,
  onChange,
  onBlur,
  showAvatar = true,
  sx,
}: UserAutocompleteProps): JSX.Element => {
  const getName = useCallback(
    (user: UserSearchResultExternal) => {
      const isAnonymous = isAnonymousUser(user);
      return fullName(isAnonymous ? t('ns.common:people:anonymous_user') : user.givenName, user?.familyName);
    },
    [t],
  );
  // Initialize inputValue from value prop on mount, then let it be controlled by user input
  const [inputValue, setInputValue] = useState(value && !isMultiple ? getName(value as UserSearchResultExternal) : '');
  const userKeyword = useDebounce<string>(inputValue, 1000);
  const search = { freeText: userKeyword, filter, pageSize };
  const { data, isFetchingNextPage, hasNextPage, fetchNextPage, status } = useInfiniteQuery({
    queryKey: queryKeys.people.users.search({ search }),
    queryFn: async ({ pageParam }) => {
      const response = await doUserSearch({
        ...search,
        page: pageParam,
      });
      return { response, nextPage: pageParam + 1 };
    },
    initialPageParam: 1,
    enabled: userKeyword === '' || userKeyword?.length > 2,
    getNextPageParam: (data) => {
      return data?.response?.summary?.totalPages >= data?.nextPage ? data?.nextPage : undefined;
    },
  });
  const mapUserToBaseAutocompleteValue = (user: UserSearchResultExternal): BaseAutocompleteValue => {
    return {
      uid: user.uid,
      label: getName(user),
      avatar: user.profilePhoto?.filePath,
    };
  };
  const opts: UserSearchResultExternal[] =
    data?.pages && data?.pages.length > 0
      ? data.pages.reduce((acc, itm) => acc.concat([...itm.response.results.filter((user) => showUser(user))]), [])
      : [];
  const additionalOptions = isMultiple ? value : [value];
  // If value is undefined or null, it will be included in the options array but we don't want that
  const userOptions = [...additionalOptions, ...opts]
    .filter((user): user is UserSearchResultExternal => Boolean(user))
    // Combine search results and selected values, removing any duplicates (by uid)
    .reduce<UserSearchResultExternal[]>((unique, user) => {
      if (!unique.some((u) => u.uid === user.uid)) {
        unique.push(user);
      }
      return unique;
    }, []);
  // Map to BaseAutocompleteValue
  const options: BaseAutocompleteValue[] = userOptions.map(mapUserToBaseAutocompleteValue);
  // Map current value to BaseAutocompleteValue
  const mappedValue = isMultiple
    ? (value as UserSearchResultExternal[]).map(mapUserToBaseAutocompleteValue)
    : value
    ? mapUserToBaseAutocompleteValue(value as UserSearchResultExternal)
    : null;
  // Determine if we should show topOfListAdornment - it should appear when there are search results
  const shouldShowTopAdornment = opts.length > 0 && topOfListAdornment;
  // Handle onChange - only called when option is clicked
  const handleChange = (newValue: BaseAutocompleteValue | BaseAutocompleteValue[] | null) => {
    if (isMultiple) {
      const newValues = (newValue as BaseAutocompleteValue[]) || [];
      const userValues = newValues
        .map((v) => userOptions.find((u) => u.uid === v.uid))
        .filter((u): u is UserSearchResultExternal => Boolean(u));
      (onChange as (users: UserSearchResultExternal[]) => void)(userValues);
    } else {
      const newVal = newValue as BaseAutocompleteValue | null;
      const userValue = newVal ? userOptions.find((u) => u.uid === newVal.uid) : null;
      if (userValue) {
        setInputValue(getName(userValue));
      } else {
        setInputValue('');
      }
      (onChange as (user: UserSearchResultExternal) => void)(userValue as UserSearchResultExternal);
    }
  };
  // Handle input change - native behavior: only update input value
  const handleInputChange = (_event: React.SyntheticEvent, newInputValue: string) => {
    setInputValue(newInputValue);
    onInputChange && onInputChange(newInputValue);
  };
  const baseProps = {
    t,
    idPrefix,
    label,
    placeholder,
    disabled,
    error,
    helperText,
    options,
    showLabel: true,
    onBlur,
    onInputChange: handleInputChange,
    inputValue,
    topOfListAdornment: shouldShowTopAdornment ? topOfListAdornment : undefined,
    loading: status === 'pending' || isFetchingNextPage,
    onScrollToBottom: () => {
      if (hasNextPage) {
        fetchNextPage();
      }
    },
    mapOptionToValue: (option: unknown) => {
      const baseValue = option as BaseAutocompleteValue;
      return {
        uid: baseValue.uid,
        label: baseValue.label || '',
        avatar: baseValue.avatar,
      };
    },
    readOnly,
    readOnlyText,
    isListAvatarVisible: showAvatar,
    fullWidth,
    filterOptions: (x: BaseAutocompleteValue[]) => x,
    closeComponent,
    sx,
    clearable: true,
    freeSolo: true,
  };
  if (isMultiple) {
    return (
      <BaseAutocomplete
        {...baseProps}
        multiple={true}
        value={mappedValue as BaseAutocompleteValue[]}
        onChange={handleChange as (value: BaseAutocompleteValue[]) => void}
      />
    );
  }
  // Use RevampedSingleUserAutocomplete for single mode - it handles user-specific logic natively
  return (
    <RevampedSingleUserAutocomplete
      idPrefix={idPrefix}
      label={label}
      placeholder={placeholder}
      disabled={disabled}
      filter={filter}
      error={error}
      helperText={helperText}
      showUser={showUser}
      onInputChange={onInputChange}
      readOnly={readOnly}
      readOnlyText={readOnlyText}
      t={t}
      pageSize={pageSize}
      value={value as UserSearchResultExternal}
      onChange={onChange as (user: UserSearchResultExternal) => void}
      onBlur={onBlur}
      topOfListAdornment={topOfListAdornment}
      showAvatar={showAvatar}
      clearable={!!value}
      forcePopupIcon={!value}
    />
  );
};
